import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {Container, Row} from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { useForm, Controller } from 'react-hook-form';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import {registerUser} from "../actions/registeredUserAction";
import MessageOverlay from "../components/MessageOverlay";
import Loader from "../components/Loader";

const RegUserRegisterScreen = () => {
  const [passwordMessage, setPasswordMessage] = useState("");
  const [successful, setSuccessful] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameMessage, setUsernameMessage] = useState("");
  const [showMessageOverlay, setShowMessageOverlay] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { register, formState: { errors }, watch, control, reset } = useForm({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      dob: ""
    }
  });
  const password = watch('password');
  const passwordConfirm = watch('passwordConfirm');
  const username = watch('username');
  const usernameConfirm = watch('usernameConfirm');
  const rewardsId = watch('rewardsId');
  const dob = watch('dob');
  const ticketNum = watch('ticketNum');
  const zipcode = watch('zipcode');

  const registeredUserDetails = useSelector(
    (state) => state.registeredUserDetails
  );
  const { userRegisterReturnMessage, userRegisterError } = registeredUserDetails;
  const dispatch = useDispatch();

  // clear submitting flag when server responds (success or error)
  useEffect(() => {
    if (submitting && (userRegisterReturnMessage || userRegisterError)) {
      setSubmitting(false);
    }
  }, [submitting, userRegisterReturnMessage, userRegisterError]);

  // Password requirements checks
  const passwordChecks = [
    {
      label: "6-40 characters long",
      test: (pw) => pw.length >= 6 && pw.length <= 40,
    },
    {
      label: "At least one uppercase letter",
      test: (pw) => /[A-Z]/.test(pw),
    },
    {
      label: "At least one lowercase letter",
      test: (pw) => /[a-z]/.test(pw),
    },
    {
      label: "At least one number",
      test: (pw) => /\d/.test(pw),
    },
    {
      label: "At least one special character",
      test: (pw) => /[@$!%*?&]/.test(pw),
    },
  ];

  const handleMessageOverlayClose = () => {
    setShowMessageOverlay(false);
  }

  useEffect(() => {
    if (password && passwordConfirm) {
      if (password !== passwordConfirm) {
        setPasswordMessage("Passwords do not match");
      } else {
        setPasswordMessage("");
      }
    }
    else if(password && !passwordConfirm) {
      setPasswordMessage("Passwords do not match");
    }
    else if(!password && passwordConfirm) {
      setPasswordMessage("Passwords do not match");
    }
    else {
      setPasswordMessage("");
    }

    if(username && usernameConfirm) {
      if (username !== usernameConfirm) {
        setUsernameMessage("Email addresses do not match");
      } else {
        setUsernameMessage("");
      }
    }
  }, [password, passwordConfirm, username, usernameConfirm]);

  useEffect(() => {
    if(userRegisterReturnMessage || userRegisterError) {
      setShowMessageOverlay(true);
    }
    if (userRegisterReturnMessage?.data && !userRegisterError) {
      reset(); // Clears all form fields
    }
  }, [userRegisterReturnMessage, userRegisterError, reset]);

  const meetsPasswordChecks = (pw) => passwordChecks.every((c) => c.test(String(pw || "")));
  const thirdFieldOk = process.env.REACT_APP_THIRD_LOGIN_FIELD === "ticketNum"
    ? !!ticketNum
    : process.env.REACT_APP_THIRD_LOGIN_FIELD === "zipcode"
      ? !!zipcode
      : true;
  const canSubmit = !submitting &&
    !!username && !!usernameConfirm && username === usernameConfirm &&
    !!rewardsId && !!dob &&
    !!password && !!passwordConfirm && password === passwordConfirm &&
    meetsPasswordChecks(password) && thirdFieldOk;

  const submitDebounceRef = useRef(0);

  const onSubmit = () => {
    // Defensive: blur the currently focused element so validation or browser quirks
    // won't immediately re-focus the first invalid field (observed issue).
    try {
      if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (e) {
      // ignore
    }
    // microtask fallback: ensure password input isn't left focused after any re-renders
    setTimeout(() => {
      try {
        const pw = typeof document !== 'undefined' && document.querySelector('input[name="password"]');
        if (pw && typeof pw.blur === 'function') pw.blur();
      } catch (e) {
        // ignore
      }
    }, 0);

    const now = Date.now();

    // Debounce to prevent multiple rapid submissions
    if (now - submitDebounceRef.current < 1000) {
      return; // Ignore if last submit was too recent
    }
    submitDebounceRef.current = now;

    setSuccessful(false);

    if (!username || !usernameConfirm || !rewardsId || !dob || !password || !passwordConfirm ||
      (process.env.REACT_APP_THIRD_LOGIN_FIELD === "ticketNum" && !ticketNum) ||
      (process.env.REACT_APP_THIRD_LOGIN_FIELD === "zipcode" && !zipcode)) {
      // Optionally set a message or handle the error
      setPasswordMessage("Please fill in all required fields.");
      return;
    }

    // Check password fields and abort if they don't match
    if (password !== passwordConfirm) {
      setPasswordMessage("Passwords do not match");
      return;
    }

    // Validate password against requirements
    const failedCheck = passwordChecks.find((check) => !check.test(password));
    if (failedCheck) {
      setPasswordMessage(`Password requirement not met: ${failedCheck.label}`);
      return;
    }

    // Check username fields and abort if they don't match
    if(username !== usernameConfirm) {
      setUsernameMessage("Email addresses do not match");
      return;
    }

    // If we got here everything is good. Clear messages and dispatch.
    setPasswordMessage("");
    setUsernameMessage("");
    setSubmitting(true);
    dispatch(registerUser(username, rewardsId, dob, ticketNum, zipcode, password));
  };

  return (
    <Container fluid className="no-padding">
      <div className="card card-container backoffice-login-card-container">
        <h2 className="login-screen-title">Digital Gift Card Claims</h2>
        <h4 className="login-screen-subtitle">User Registration</h4>
        {submitting && (
          <div>
            <Loader />
            <strong>Registering your account. Please wait.</strong>
          </div>
        )}
        <MessageOverlay
          show={showMessageOverlay}
          handleClose={handleMessageOverlayClose}
          message={userRegisterError ? userRegisterError : userRegisterReturnMessage?.data}
          title={userRegisterError ? "Error" : "Activation Required"}
        />
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="gcotg-form" noValidate>
          {!successful && (
            <div>
              <div className="form-group gcotg-form-group">
                <label htmlFor="username">Email Address</label>
                <input
                  type="text"
                  className="form-control"
                  {...register('username', {
                    required: true,
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                  })}
                />
                <label htmlFor="usernameConfirm">Confirm Email Address</label>
                <input
                  type="text"
                  className="form-control"
                  {...register('usernameConfirm', {
                    required: true,
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  })}
                />
                {(errors.username || errors.usernameConfirm) && (
                  <p className="gcotg-error-message">Must be a valid email address</p>
                )}
                {usernameMessage && (
                  <div className="form-group gcotg-error-message" role="alert">
                    {usernameMessage}
                  </div>
                )}
              </div>

              <div className="form-group gcotg-form-group">
                <label htmlFor="rewardsId">Rewards Number</label>
                <input
                  type="text"
                  className="form-control"
                  {...register('rewardsId', {
                    required: true
                  })}
                />
                {errors.rewardsId && <p className="gcotg-error-message">This field is required</p>}
              </div>

              <div className="form-group gcotg-form-group">
                <label htmlFor="dob">Date of Birth (MM/DD/YYYY)</label>
                <Controller
                  name="dob"
                  control={control}
                  rules={{
                    required: "Date of birth is required",
                    pattern: {
                      value: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
                      message: "Enter a valid date (MM/DD/YYYY)"
                    }
                  }}
                  render={({ field }) => (
                    <InputMask
                      mask="99/99/9999"
                      maskChar="_"
                      placeholder="mm/dd/yyyy"
                      {...field}
                      className="form-control gcotg-form-element"
                    />
                  )}
                />
                {errors.dob && <p className="gcotg-error-message">{errors.dob.message}</p>}
              </div>

              {process.env.REACT_APP_THIRD_LOGIN_FIELD === "ticketNum" && (
                <div className="form-group gcotg-form-group">
                  <label htmlFor="ticketNum" className="gcotg-form-label">
                    Ticket # from a previous gift card promotion
                  </label>
                  <input
                    id="ticketNum"
                    type="text"
                    {...register('ticketNum', {
                      required: true
                    })}
                    className="form-control gcotg-form-element"
                  />
                </div>
              )}
              {process.env.REACT_APP_THIRD_LOGIN_FIELD === "zipcode" && (
                <div className="form-group gcotg-form-group">
                  <label htmlFor="zipcode" className="gcotg-form-label">ZIP Code</label>
                  <input
                    id="zipcode"
                    type="text"
                    {...register('zipcode', {
                      required: true
                    })}
                    className="form-control gcotg-form-element login-zipcode-form-element"
                  />
                </div>
              )}

              <div className="form-group gcotg-form-group">
                <label htmlFor="password">Choose a Password</label>
                <div style={{position: "relative"}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    style={{paddingRight: "2.5rem"}}
                    {...register('password', {
                      required: true,
                      minLength: 6,
                      maxLength: 40,
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
                    })}
                  />
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#888"
                    }}
                    tabIndex={-1}
                    role="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye}/>
                  </span>
                </div>
                <label htmlFor="password-confirm">Confirm Password</label>
                <div style={{position: "relative"}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    style={{paddingRight: "2.5rem"}}
                    {...register('passwordConfirm', {
                      required: true,
                      minLength: 6,
                      maxLength: 40,
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
                    })}
                  />
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#888"
                    }}
                    tabIndex={-1}
                    role="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye}/>
                  </span>
                </div>
                <ul className="password-requirements">
                  {errors.password && passwordChecks.map((req, idx) => {
                    const passed = req.test(password || "");
                    return (
                      <li key={idx} style={{color: passed ? "green" : "red", display: "flex", alignItems: "center"}}>
                        <FontAwesomeIcon
                          icon={passed ? faCheckCircle : faTimesCircle}
                          style={{marginRight: 8}}
                        />
                        {req.label}
                      </li>
                    );
                  })}
                </ul>
                {passwordMessage && (
                  <div className="form-group gcotg-error-message" role="alert">
                    {passwordMessage}
                  </div>
                )}
              </div>

              <div className="form-group">
                <button
                  type="submit"
                  className="btn btn-primary btn-block gcotg-button"
                  disabled={submitting || !canSubmit}
                >
                  Sign Up
                </button>
              </div>
              <Row>
                <p className="gcotg-link-paragraph">
                  Already have an account? <a href="/registeredlogin">Login here</a><br />
                  <span className="registered-login-screen-guest-link">
                    or <a href="/">continue as a guest</a>
                  </span>
                </p>
              </Row>
            </div>
          )}

        </form>
      </div>
    </Container>
  );
};

export default RegUserRegisterScreen;
